import type { OiDetail, OiSummary } from '@/lib/api/types';
import type { DroneLive } from '@/lib/store/drones';

/** One OiSummary from the drone's live MQTT OI envelope. Null until the
 *  aircraft has published an OI id. */
export function buildOiSummaryFromDrone(d: DroneLive): OiSummary | null {
  const oi = d.oi;
  if (!oi || !oi.oi_id) return null;
  return {
    oi_id: oi.oi_id,
    // `mission_name` (rich networkid) is the human-readable label —
    // e.g. "SIMTL70 Orin SITL testing". Falls back to empty string so
    // the OI card title row gracefully hides when unavailable.
    title: oi.mission_name ?? '',
    status: oi.status,
    aircraft: d.id,
    drone_model: d.telemetry.drone_model ?? '—',
    start_utc: oi.start_utc ?? '',
    end_utc: oi.end_utc ?? '',
    pilot_name: oi.pilot_name ?? '—',
    ops_approval: oi.ops_approval ?? '—',
    op_type: oi.op_type ?? '—',
    legal_rule: oi.legal_rule ?? '—',
    submission_date: null,
    polygon: oi.polygon ?? undefined,
    subscriber_count: 0,
    last_updated_utc: oi.received_at,
    // The OI topic carries no tenant field of its own — a declaration is
    // *about* an aircraft, so provenance comes from the aircraft. This is
    // the only join available and it is the correct one: an OI for a
    // partner's drone is a partner OI by definition.
    origin: d.origin,
    partner_tenant: d.partnerTenant,
  };
}

/** An OiDetail for an OI no aircraft has linked to yet (a Scheduled FlightBlender
 *  declaration): the summary's own fields plus '—' / null for everything only
 *  the DSS or a flying aircraft would carry. Null without a polygon. */
export function buildOiDetailFromSummary(s: OiSummary): OiDetail | null {
  if (!s.polygon) return null;
  const ring = s.polygon.coordinates[0] ?? [];
  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  for (const [lng, lat] of ring) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const durationMin = Math.max(
    0,
    Math.round((Date.parse(s.end_utc) - Date.parse(s.start_utc)) / 60000) || 0,
  );
  return {
    ...s,
    polygon: s.polygon,
    polygon_bounds: ring.length > 0 ? [minLng, minLat, maxLng, maxLat] : [0, 0, 0, 0],
    polygon_vertex_count: ring.length,
    operation: s.op_type,
    mission_id: null,
    flight_id: null,
    plan_id: s.title || null,
    dss_status: '—',
    dss_status_code: null,
    oi_ref_id: s.oi_id,
    ovn: '—',
    alt_lower_m: null,
    alt_upper_m: null,
    max_agl_m: null,
    uss_availability: '—',
    uss_base_url: '—',
    subscription_id: null,
    subscribers: [],
    duration_min: durationMin,
    submission_status: s.ops_approval,
    flight_decl_id: s.oi_id,
    manager_uss: null,
    last_updated: s.last_updated_utc ?? null,
  };
}
